import React from 'react';
import { motion } from 'framer-motion';
import { BookOpenIcon, CheckIcon, DownloadIcon, EyeIcon, GiftIcon, LayersIcon, SparklesIcon } from 'lucide-react';
import { CheckoutButton } from './Button';
import { ProductName } from './ProductName';
import { Ornament } from './Ornament';
import { PriceTag } from './PriceTag';
const includes = [{
  label: 'Volume 1 — Stretching Foundations & Essentials'
}, {
  label: 'Volume 2 — Lower Body Flexibility & Mobility'
}, {
  label: 'Volume 3 — Upper Body Flexibility & Mobility'
}, {
  label: 'Volume 4 — Dynamic Stretching & Active Mobility'
}, {
  label: 'Volume 5 — Flexibility for Health & Performance'
}, {
  label: 'Volume 6 — Progressive Flexibility & Multi-Level Programming'
}, {
  label: '150 Ready-to-Teach Stretching Classes'
}, {
  label: '100% Visual Core Teaching System'
}, {
  label: 'Beginner-to-Advanced Progressions'
}, {
  label: 'Instructor Coaching Cues'
}, {
  label: 'Modifications & Level-Up Options'
}, {
  label: 'Visual Corrections'
}, {
  label: 'Ready-to-Teach Class Structures'
}, {
  label: 'Stretching Instructor Cueing & Communication Playbook',
  bonus: true
}, {
  label: 'Stretching Modifications & Progressions Handbook',
  bonus: true
}, {
  label: 'Stretching Class Planning & Sequencing System',
  bonus: true
}, {
  label: 'Stretching Safety, Contraindications & Instructor Decision Guide',
  bonus: true
}, {
  label: 'Stretching Instructor Assessment & Progress Tracking Handbook',
  bonus: true
}, {
  label: 'Immediate Digital Access'
}, {
  label: 'PDF Format'
}];
const alternatives = ['Lower-body programming.', 'Upper-body programming.', 'Dynamic stretching.', 'Active mobility.', 'Recovery.', 'Full-body stretching.', 'Progressive programming.', '150 complete classes.', 'Visual teaching tools.', 'Modifications.', 'Corrections.', 'Instructor cues.'];
const trustItems = [{
  icon: LayersIcon,
  label: '6 Complete Instructor Volumes'
}, {
  icon: BookOpenIcon,
  label: '150 Ready-to-Teach Classes'
}, {
  icon: EyeIcon,
  label: '100% Visual Core Material'
}, {
  icon: GiftIcon,
  label: '5 Professional Bonus Guides'
}, {
  icon: DownloadIcon,
  label: 'Immediate Access'
}];
const alreadyHave = ['You already have the class structure.', 'You already have the sequence.', 'You already have the visual teaching tools.', 'You already have the modifications.', 'You already have the progressions.'];
export function PricingSection() {
  return <section id="pricing" className="bg-inverse px-4 py-20 text-inverse-foreground sm:py-28" data-id="element-243" data-mp="3iiabr6ebrg0">
      <div className="mx-auto max-w-3xl" data-id="element-244" data-mp="3kz08dapbxlep">
        <div className="text-center" data-id="element-245" data-mp="3l01incyiu3vd">
          <p className="text-sm font-semibold uppercase tracking-[0.18em] text-inverse-foreground/70" data-id="element-246" data-mp="3pxl16p2952t4">
            Your Complete Stretching Instructor Ready Package
          </p>
          <h2 className="mt-3 text-3xl font-semibold text-inverse-foreground sm:text-4xl" data-id="element-247" data-mp="3bzwmy02owtx1">
            A Complete 6-in-1 Visual Teaching System —{' '}
            <span className="italic text-accent-soft" data-id="element-248" data-mp="2xc4qp9ofpk2t">Ready for Your Next Class</span>
          </h2>
          <Ornament className="mt-5" data-id="element-249" data-mp="2sd1wyldwn0a5" />
        </div>
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.3,
        ease: [0.23, 1, 0.32, 1]
      }} className="relative mt-12 rounded-2xl border border-accent/50 bg-inverse-raised shadow-elevated" data-id="element-250" data-mp="2seczyizj0407">
          <div className="absolute -top-3.5 left-1/2 inline-flex -translate-x-1/2 items-center gap-1.5 whitespace-nowrap rounded-full border border-accent bg-accent px-3.5 py-1 text-xs font-semibold uppercase tracking-[0.1em] text-foreground" data-id="element-251" data-mp="3bbe82codmk8d">
            <SparklesIcon size={14} className="text-foreground" aria-hidden="true" data-id="element-252" data-mp="3l1h8gxxtv0gq" />{' '}
            6-in-1 System
          </div>
          <div className="p-6 text-center sm:p-10" data-id="element-253" data-mp="3u1ds2l5d194x">
            <h3 className="text-3xl font-semibold text-inverse-foreground" data-id="element-254" data-mp="3u2ork8rrvuq8">
              <ProductName accentClassName="text-accent-soft" data-id="element-255" data-mp="hyvuw6gft2lk" />
            </h3>
            <p className="mt-3 text-xs font-bold tracking-[0.16em] text-accent" data-id="element-256" data-mp="lzftzfkkmgac">
              6 VOLUMES. 150 CLASSES. 5 BONUSES. ONE COMPLETE SYSTEM.
            </p>
            <p className="mt-7 text-left text-sm font-semibold text-inverse-foreground sm:mx-auto sm:max-w-xl" data-id="element-257" data-mp="qsha1qwiklvi">
              You Receive:
            </p>
            <ul className="mx-auto mt-3 grid max-w-xl gap-y-2.5 border-y border-accent/25 py-6 text-left" data-id="element-258" data-mp="3v5nvzx2shybo">
              {includes.map((item, index) => {
              const Icon = item.bonus ? GiftIcon : CheckIcon;
              const startsBonuses = item.bonus && !includes[index - 1]?.bonus;
              return <React.Fragment key={item.label} data-id="element-259" data-mp="cwwsozwfobq7" data-mpi={index}>
                    {startsBonuses && <li className="pt-3 text-sm font-semibold text-accent-soft" data-id="element-260" data-mp="1jh64j732dsya">
                        PLUS:
                      </li>}
                    <li className="flex gap-2.5 text-sm leading-6 text-inverse-foreground/85" data-id="element-261" data-mp="38v5qrczsfi11">
                      <Icon size={16} strokeWidth={1.75} className="mt-1 shrink-0 text-accent" aria-hidden="true" data-id="element-262" data-mp="33y5j4xnai77t" />
                      <span data-id="element-263" data-mp="2u716i4l36yg5">
                        {item.bonus && <span className="mr-1.5 rounded-sm bg-accent px-1.5 py-0.5 text-[10px] font-bold tracking-[0.08em] text-foreground" data-id="element-264" data-mp="2q6h7swkv7mrw">
                            BONUS
                          </span>}
                        {item.label}
                      </span>
                    </li>
                  </React.Fragment>;
            })}
            </ul>

            <div className="mx-auto mt-8 max-w-xl text-left" data-id="element-265" data-mp="eevcxbfi0pow">
              <p className="text-sm leading-6 text-inverse-foreground/70" data-id="element-266" data-mp="9gk6zbsbqzby">
                You could continue collecting random routines, saved videos
                and disconnected class ideas.
              </p>
              <p className="mt-3 text-sm font-semibold text-accent-soft" data-id="element-267" data-mp="28oag4i5j0qsa">
                Or you can already have:
              </p>
              <ul className="mt-3 flex flex-wrap gap-1.5" data-id="element-268" data-mp="1u0xpif0v3uv1">
                {alternatives.map(item => <li key={item} className="rounded-full border border-accent/30 bg-inverse-foreground/5 px-2.5 py-1 text-xs font-medium text-inverse-foreground" data-id="element-269" data-mp="1yw4m72vprzag">
                    {item}
                  </li>)}
              </ul>
              <p className="mt-3 text-sm leading-6 text-inverse-foreground/70" data-id="element-270" data-mp="2dpg7abjjg5zp">
                All organized inside one professional 6-in-1 stretching
                teaching system.
              </p>
            </div>

            <PriceTag label="Today" tone="dark" className="mt-10" data-id="element-271" data-mp="169a58fy3vi1q" />
            <div data-id="element-272" data-mp="167fhktvg9bty">
              <CheckoutButton variant="inverse" className="mt-7 w-full sm:w-auto" data-id="element-273" data-mp="yq0uai9ybbak">
                GET THE COMPLETE 6-IN-1 STRETCHING TEACHING SYSTEM
              </CheckoutButton>
            </div>
            <div className="mt-6 flex flex-wrap justify-center gap-x-5 gap-y-2 text-xs font-medium text-inverse-foreground/70" data-id="element-274" data-mp="18o6vexnxoded">
              {trustItems.map(({
              icon: Icon,
              label
            }) => <span key={label} className="flex items-center gap-1.5" data-id="element-275" data-mp="13n9eeoentpeg">
                  <Icon size={14} strokeWidth={1.75} className="text-accent" aria-hidden="true" data-id="element-276" data-mp="g017k21l28ab" />
                  {label}
                </span>)}
            </div>
          </div>
        </motion.div>

        <div className="mx-auto mt-14 max-w-2xl text-center" data-id="element-277" data-mp="prff8x3oueej">
          <p className="font-display text-2xl font-semibold text-inverse-foreground sm:text-3xl" data-id="element-278" data-mp="kxkx3y3qe7hk">
            Stretching Instructors Do Not Need More Random Stretches
          </p>
          <p className="mt-3 font-display text-xl italic text-accent-soft" data-id="element-279" data-mp="3ofunofs05hqu">
            They need structure. They need variety. They need progression. And
            they need a system they can actually use.
          </p>
          <p className="mt-5 text-base leading-7 text-inverse-foreground/70" data-id="element-280" data-mp="37vw2jr2dbdeo">
            With Stretching Instructor Ready, you do not have to begin every class
            with an empty page. {alreadyHave.join(' ')}
          </p>
          <p className="mt-4 text-base font-medium leading-7 text-inverse-foreground" data-id="element-281" data-mp="3gtold57at36t">
            And with 150 ready-to-teach classes, you already have the class.
          </p>
        </div>
      </div>
    </section>;
}